import { ChunkedArray, PAGE_LIMIT, getJob, jobStatus, openRowReader, readPage, scanRows } from './dataset.js'
import { duplicateStatus, getDuplicateJob, readDuplicatePage } from './duplicates.js'
import { filterKey } from './filters.js'
import { resolutionKey, toTimestamp } from './resolve.js'
import { httpError } from './errors.js'

/**
 * Compare the view against an uploaded CSV, and say what is different.
 *
 * The view is the base. Every verdict is phrased as what the uploaded file does
 * relative to the rows you are looking at, never the other way round:
 *
 *   same      matched on the key, and every compared column agrees
 *   changed   matched on the key, but at least one compared column differs —
 *             and which ones, with both values, is the point of the whole thing
 *   missing   in the view, with no counterpart in the file at all
 *   extra     in the file, with no counterpart in the view
 *   repeat    a row whose key repeats an earlier row on its own side, so it was
 *             never paired with anything — flagged rather than mis-paired
 *
 * Those five partition both files exactly once: every view row is same, changed,
 * missing or a view-side repeat, and every uploaded row is same, changed, extra
 * or a file-side repeat.
 *
 * "The view" means whatever the table is showing — the filters, and the duplicate
 * mode and resolution rules if those are in play. That is not a special case
 * here: the view's rows are read back through the very readers the table pages
 * through, so a comparison against deduplicated-and-merged records costs one
 * extra walk rather than a second implementation of dedupe.
 *
 * ## How it stays memory-bounded
 *
 * Two passes, and neither holds a row:
 *
 *   1. Stream the uploaded file once and index it by key. Per row that is a key
 *      string plus five numbers in typed arrays — the row's number, its byte
 *      offset, a 64-bit hash of its compared values, and how many times the view
 *      has hit it. The values themselves are dropped.
 *   2. Walk the view's rows in order, hash each one's compared values the same
 *      way, and look its key up. The verdict falls straight out of the two
 *      hashes: equal means same, different means changed.
 *
 * So the scan never reads a row of one file while positioned in the other, which
 * is what a naive implementation would do once per row and what would make this
 * unusable on a large file.
 *
 * The per-cell diff you actually read — this column, this value on each side — is
 * computed from the real values when a page is requested, not from the hashes. A
 * 64-bit collision would have to happen for a `same` verdict to be wrong about a
 * pair, and a page reports what the values say regardless.
 *
 * A blank rule that lets the file's blanks pass (see BLANK_RULES) cannot be
 * expressed as "hash everything", so pass 1 also records which of the compared
 * columns are blank on the file's side, and pass 2 skips exactly those — the ones
 * the cell comparison is going to ignore anyway. The hash and the cell-by-cell
 * answer therefore agree by construction rather than by luck.
 *
 * That asymmetry is why there is no "ignore blanks on either side" rule. Ignoring
 * the *view's* blanks would mean dropping columns from the file's side of the
 * hash after it was computed, which needs a hash per column per row rather than
 * one per row — and a rule whose count disagreed with the rows it listed would be
 * worse than not having the rule. The view is the base, so the rule that matters
 * is the one about the file, and that one is exact.
 */

/** Distinct keys tracked exactly on the uploaded side. Past this: partial. */
const MAX_KEYS = 2_000_000
/** Compare jobs cached per view dataset. Each retains a slot per row of both sides. */
const MAX_COMPARE_JOBS = 2
/** Rows per batch when walking the view, and when reading a page back. */
const WALK_PAGE = 500
/** Positions this far apart are still worth reading in one go. */
const RUN_GAP = 64

const NONE = 0xffffffff

const V_SAME = 0
const V_CHANGED = 1
const V_MISSING = 2
const V_EXTRA = 3
const V_REPEAT_VIEW = 4
const V_REPEAT_FILE = 5

const VERDICT_NAME = ['same', 'changed', 'missing', 'extra', 'repeat', 'repeat']
/** Which file a row came from. Both sides exist only for a matched pair. */
const VERDICT_SIDE = ['both', 'both', 'view', 'file', 'view', 'file']

/** What the table lists. Every mode is a filter over one finished scan. */
export const COMPARE_MODES = ['all', 'changed', 'missing', 'extra', 'same', 'repeat']

const MODE_VERDICTS = {
  all: [V_SAME, V_CHANGED, V_MISSING, V_EXTRA, V_REPEAT_VIEW, V_REPEAT_FILE],
  changed: [V_CHANGED],
  missing: [V_MISSING],
  extra: [V_EXTRA],
  same: [V_SAME],
  repeat: [V_REPEAT_VIEW, V_REPEAT_FILE],
}

/**
 * What a blank cell means. The middle one is the rule most real comparisons
 * want: an export that simply does not carry a column is not evidence that the
 * field was cleared.
 */
export const BLANK_RULES = [
  {
    id: 'strict',
    label: 'a blank is a difference',
    hint: 'An empty cell on one side and a value on the other counts as changed.',
  },
  {
    id: 'file',
    label: 'ignore blanks in the uploaded file',
    hint: 'A column the file leaves empty is read as "no answer" rather than as a cleared field. A value the file adds where the view is blank is still a change.',
  },
]

const BLANK_IDS = new Set(BLANK_RULES.map((b) => b.id))

export function normalizeCompareMode(mode) {
  if (mode === undefined || mode === null || mode === '') return 'all'
  if (!COMPARE_MODES.includes(mode)) throw httpError(400, `Unknown comparison mode: ${mode}`)
  return mode
}

/* ------------------------------------------------------------------ the spec */

/** Header names reduced to what a human would call the same column. */
const loose = (name) => String(name ?? '').toLowerCase().replace(/[^a-z0-9]/g, '')

/**
 * Pair up the two files' columns by name: exact first, then ignoring case,
 * spaces and punctuation, so `BCL Status` finds `bcl_status`. Only ever a
 * starting point — the panel shows every pairing and lets it be changed, because
 * no name-matching rule is going to be right about somebody's export.
 *
 * @returns {Record<string, string>} view column name -> file column name
 */
export function autoMap(viewHeaders, fileHeaders) {
  const taken = new Set()
  const byLoose = new Map()
  for (const name of fileHeaders) {
    const key = loose(name)
    if (!byLoose.has(key)) byLoose.set(key, name)
  }

  const map = {}
  for (const name of viewHeaders) {
    if (fileHeaders.includes(name) && !taken.has(name)) {
      map[name] = name
      taken.add(name)
    }
  }
  for (const name of viewHeaders) {
    if (map[name]) continue
    const hit = byLoose.get(loose(name))
    if (hit && !taken.has(hit)) {
      map[name] = hit
      taken.add(hit)
    }
  }
  return map
}

const DEFAULT_OPTIONS = {
  trim: true,
  ignoreCase: false,
  numeric: false,
  dates: false,
  dayFirst: false,
  blanks: 'strict',
}

/**
 * Validate and canonicalize what the client sent, against both files' headers.
 *
 * @returns {{keyColumns, keyPairs, mapped, compared, options}} column names for
 *   display, and index pairs for the scan
 */
export function normalizeCompare(input, viewHeaders, fileHeaders) {
  if (!input || typeof input !== 'object' || Array.isArray(input)) {
    throw httpError(400, 'compare must be an object.')
  }

  const map = input.map && typeof input.map === 'object' && !Array.isArray(input.map)
    ? input.map
    : autoMap(viewHeaders, fileHeaders)

  const pairOf = (name) => {
    const a = viewHeaders.indexOf(name)
    if (a < 0) throw httpError(400, `Unknown column: ${name}`)
    const other = map[name]
    if (typeof other !== 'string' || other === '') {
      throw httpError(400, `“${name}” is not matched to a column in the uploaded file.`)
    }
    const b = fileHeaders.indexOf(other)
    if (b < 0) throw httpError(400, `The uploaded file has no column called “${other}”.`)
    return { name, other, a, b }
  }

  const rawKeys = Array.isArray(input.keyColumns) ? input.keyColumns : []
  if (rawKeys.length === 0) throw httpError(400, 'Choose at least one column to match rows on.')
  const keyPairs = []
  const seenKeys = new Set()
  for (const name of rawKeys) {
    if (seenKeys.has(name)) continue
    seenKeys.add(name)
    keyPairs.push(pairOf(name))
  }

  // Every mapped column that is not part of the key, unless the caller narrowed
  // it. A key column is what makes two rows the same record, so comparing it
  // against itself would only ever report "same".
  const mapped = []
  for (const name of viewHeaders) {
    if (typeof map[name] !== 'string' || map[name] === '') continue
    if (fileHeaders.indexOf(map[name]) < 0) continue
    mapped.push(pairOf(name))
  }

  let compared
  if (Array.isArray(input.columns)) {
    const wanted = new Set(input.columns)
    compared = mapped.filter((p) => wanted.has(p.name) && !seenKeys.has(p.name))
    const unknown = input.columns.filter((c) => !mapped.some((p) => p.name === c) && !seenKeys.has(c))
    if (unknown.length > 0) {
      throw httpError(400, `Cannot compare ${unknown.join(', ')} — not matched to a column in the uploaded file.`)
    }
  } else {
    compared = mapped.filter((p) => !seenKeys.has(p.name))
  }

  const raw = input.options && typeof input.options === 'object' ? input.options : {}
  const blanks = raw.blanks === undefined ? DEFAULT_OPTIONS.blanks : raw.blanks
  if (!BLANK_IDS.has(blanks)) throw httpError(400, `Unknown blank rule: ${blanks}`)
  const options = {
    trim: raw.trim === undefined ? DEFAULT_OPTIONS.trim : !!raw.trim,
    ignoreCase: !!raw.ignoreCase,
    numeric: !!raw.numeric,
    dates: !!raw.dates,
    dayFirst: !!raw.dayFirst,
    blanks,
  }

  return { keyColumns: keyPairs.map((p) => p.name), keyPairs, mapped, compared, options }
}

/**
 * Stable cache key. The mode is deliberately absent: every mode is a filter over
 * the same finished scan, so flipping between "what changed" and "what is
 * missing" must not re-read two files.
 */
export function compareKey(spec, dup, fileId, cmp, ds = null) {
  const dupPart = dup?.columns?.length
    ? JSON.stringify([dup.columns, dup.mode ?? 'duplicates', resolutionKey(dup.resolution)])
    : ''
  return JSON.stringify([
    filterKey(spec, ds),
    dupPart,
    fileId,
    cmp.keyColumns,
    cmp.compared.map((p) => [p.name, p.other]),
    cmp.options,
  ])
}

/** One line for the scope trail. */
export function describeCompare(cmp) {
  const bits = [`on ${cmp.keyColumns.join(' + ')}`]
  bits.push(`${cmp.compared.length} column${cmp.compared.length === 1 ? '' : 's'} compared`)
  const on = []
  if (cmp.options.trim) on.push('trimmed')
  if (cmp.options.ignoreCase) on.push('case-insensitive')
  if (cmp.options.numeric) on.push('numbers')
  if (cmp.options.dates) on.push('dates')
  if (cmp.options.blanks !== 'strict') on.push(cmp.options.blanks === 'file' ? 'file blanks ignored' : 'blanks ignored')
  if (on.length) bits.push(on.join(', '))
  return bits.join(' · ')
}

/* ------------------------------------------------- normalizing and comparing */

const NUMERIC = /^[+-]?(\d+\.?\d*|\.\d+)([eE][+-]?\d+)?$/

/**
 * One cell reduced to the form the comparison actually cares about.
 *
 * Where a value can be read as more than one kind of thing, the kind is part of
 * the normal form: with numeric matching on, `1.0` and `1` both become the number
 * 1, and the literal text `#1` becomes `=#1`, so a value can never be equal to
 * another value it does not mean. Without those modes there is nothing to tag and
 * nothing is allocated, which is the common path.
 */
export function makeNormalizer(options) {
  const { trim, ignoreCase, numeric, dates, dayFirst } = options
  const tagged = numeric || dates
  return (raw) => {
    let v = raw ?? ''
    if (trim) v = v.trim()
    if (v === '') return ''
    // Numbers before dates: `20260211` is legitimately both, and whichever is
    // chosen, both sides are read the same way, so equality stays consistent.
    if (numeric && NUMERIC.test(v)) return `#${Number(v)}`
    if (dates) {
      const t = toTimestamp(v, dayFirst)
      if (t !== null) return `@${t}`
    }
    if (ignoreCase) v = v.toLowerCase()
    return tagged ? `=${v}` : v
  }
}

/**
 * Whether one column agrees, with the blank rule applied. `file` is the
 * uploaded side, which is the only side the `file` rule is about.
 */
export function sameValue(viewRaw, fileRaw, norm, blanks) {
  const a = norm(viewRaw)
  const b = norm(fileRaw)
  if (a === b) return true
  return blanks === 'file' && b === ''
}

const FNV_PRIME = 0x01000193
const SEED_A = 0x811c9dc5
const SEED_B = 0x9e3779b9

/** FNV-1a over a string's code units, with the length mixed in. */
function fnv(h, s) {
  for (let i = 0; i < s.length; i++) h = Math.imul(h ^ s.charCodeAt(i), FNV_PRIME)
  return Math.imul(h ^ s.length, FNV_PRIME)
}

/** A different mixer, so the two halves of the 64-bit hash fail independently. */
function djb(h, s) {
  for (let i = 0; i < s.length; i++) h = (Math.imul(h, 33) ^ s.charCodeAt(i)) >>> 0
  return (Math.imul(h, 33) ^ s.length) >>> 0
}

/**
 * 64-bit hash of a row's compared values, as two 32-bit halves written into
 * `out`. `skip` — when given — is a bitmask of compared columns to leave out,
 * which is how a blank rule stays exactly as strict as the cell comparison.
 *
 * The column's position is mixed in, so moving a value from one column to
 * another is a difference rather than a coincidence.
 */
function contentHash(get, count, norm, skip, out) {
  let a = SEED_A
  let b = SEED_B
  for (let k = 0; k < count; k++) {
    if (skip && (skip[k >>> 5] & (1 << (k & 31))) !== 0) continue
    const v = norm(get(k))
    a = fnv(fnv(a, `${k}`), v)
    b = djb(djb(b, `${k}`), v)
  }
  out[0] = a >>> 0
  out[1] = b >>> 0
}

/** Exact key, length-prefixed so ('a','bc') cannot be forged into ('ab','c'). */
function buildKey(get, count, norm) {
  let key = ''
  for (let k = 0; k < count; k++) {
    const v = norm(get(k))
    key += `${v.length}:${v}`
  }
  return key
}

/* ---------------------------------------------------------- the view's rows */

/**
 * The rows the table is showing, addressed by position.
 *
 * Reading them back through the same readers the table pages through is what
 * makes "the view" mean the same thing here as it does on screen — filtered,
 * deduplicated, merged, or the plain file.
 */
function viewSource(ds, spec, dup) {
  if (dup?.columns?.length) {
    const job = getDuplicateJob(ds, spec, dup.columns, dup.mode, dup.resolution)
    return {
      done: () => job.done,
      error: () => job.error,
      count: () => job.available,
      capped: () => job.capped,
      progress: () => duplicateStatus(job).progress,
      page: (offset, limit) => readDuplicatePage(ds, job, offset, Math.min(limit, PAGE_LIMIT)),
    }
  }
  const job = getJob(ds, spec)
  return {
    done: () => (job ? job.done : ds.indexed),
    error: () => (job ? job.error : ds.indexError),
    count: () => (job ? job.retained : ds.rows),
    capped: () => (job ? job.capped : false),
    progress: () => jobStatus(ds, job).progress,
    page: (offset, limit) => readPage(ds, job, offset, Math.min(limit, PAGE_LIMIT)),
  }
}

/** Wait for a background scan the comparison depends on, or fail with its error. */
function waitFor(source, job) {
  return new Promise((resolve, reject) => {
    const tick = () => {
      if (job.cancelled) return reject(new Error('Comparison cancelled.'))
      const err = source.error()
      if (err) return reject(new Error(err))
      if (source.done()) return resolve()
      setTimeout(tick, 40)
    }
    tick()
  })
}

/* -------------------------------------------------------------------- the job */

function startCompareJob(dsView, dsFile, spec, dup, cmp, key) {
  const view = viewSource(dsView, spec, dup)
  const norm = makeNormalizer(cmp.options)
  const { keyPairs, compared, options } = cmp
  const words = Math.max(1, Math.ceil(compared.length / 32)) // blank mask, per row
  const tracksBlanks = options.blanks !== 'strict'

  const job = {
    key,
    dsFile,
    cmp,
    view,
    norm,
    cancelled: false,
    phase: 'view', // view -> file -> match -> done
    progress: 0,
    viewRows: 0,
    fileRows: 0,
    walked: 0,
    counts: { same: 0, changed: 0, missing: 0, extra: 0, repeatView: 0, repeatFile: 0 },
    capped: false,
    keysCapped: false,
    done: false,
    error: null,
    touched: Date.now(),
    // One entry per result row, in view order and then file order.
    vPos: new ChunkedArray(Uint32Array), // position in the view's row list
    vRow: new ChunkedArray(Uint32Array), // the view row's number in its file
    fRow: new ChunkedArray(Uint32Array),
    fOff: new ChunkedArray(Float64Array),
    verdict: new ChunkedArray(Uint8Array),
    total: 0,
    orders: new Map(), // mode -> Uint32Array of result indices
  }

  /*
   * The uploaded file, indexed by key. All of this is scratch for the two passes
   * and is released the moment the walk finishes — the key map is the expensive
   * part, and holding it for the life of a browsable result would dwarf the
   * result itself. `let`, not `const`, so it can actually be dropped.
   */
  let keys = new Map() // key string -> entry index
  let fRowOf = new ChunkedArray(Uint32Array)
  let fOffOf = new ChunkedArray(Float64Array)
  let fH1 = new ChunkedArray(Uint32Array)
  let fH2 = new ChunkedArray(Uint32Array)
  let fHits = new ChunkedArray(Uint32Array)
  let fBlank = tracksBlanks ? new ChunkedArray(Uint32Array) : null
  let entries = 0

  // Rows of the uploaded file whose key repeated an earlier one: they were never
  // paired with anything, so they are listed rather than silently folded in.
  let repRow = new ChunkedArray(Uint32Array)
  let repOff = new ChunkedArray(Float64Array)
  let repeats = 0

  /** Everything the two passes needed and nothing a page will. */
  const release = () => {
    keys = null
    fRowOf = null
    fOffOf = null
    fH1 = null
    fH2 = null
    fHits = null
    fBlank = null
    repRow = null
    repOff = null
    job.scan = null
  }

  const hash = new Uint32Array(2)
  const mask = new Uint32Array(words)

  const push = (verdict, pos, viewRow, fileRow, fileOffset) => {
    job.vPos.push(pos)
    job.vRow.push(viewRow)
    job.fRow.push(fileRow)
    job.fOff.push(fileOffset)
    job.verdict.push(verdict)
    job.total++
  }

  /** Pass 1: stream the uploaded file and index it by key. */
  const indexFile = () => {
    const bytes = Math.max(1, dsFile.size - dsFile.headerEnd)
    let read = 0
    return scanRows(dsFile, {
      onChunk: (n) => {
        read += n
        job.progress = Math.min(1, read / bytes)
      },
      onRecord: (c) => {
        job.fileRows = c.index + 1
        const key = buildKey((k) => c.get(keyPairs[k].b), keyPairs.length, norm)
        if (keys.has(key)) {
          // A key the file itself repeats. Pairing the view's row with an
          // arbitrary one of them would invent an answer, so every copy after the
          // first is listed as a repeat on the file's side instead.
          repRow.push(c.index)
          repOff.push(c.startOffset)
          repeats++
          return
        }
        if (keys.size >= MAX_KEYS) {
          // No room to track this key exactly. Dropping it would report every one
          // of its rows as `extra` and the matching view rows as `missing` —
          // twice wrong — so it is left out and the result is flagged partial.
          job.keysCapped = true
          return
        }
        keys.set(key, entries)

        if (tracksBlanks) {
          mask.fill(0)
          for (let k = 0; k < compared.length; k++) {
            if (norm(c.get(compared[k].b)) === '') mask[k >>> 5] |= 1 << (k & 31)
          }
          for (let w = 0; w < words; w++) fBlank.push(mask[w])
          contentHash((k) => c.get(compared[k].b), compared.length, norm, mask, hash)
        } else {
          contentHash((k) => c.get(compared[k].b), compared.length, norm, null, hash)
        }

        fRowOf.push(c.index)
        fOffOf.push(c.startOffset)
        fH1.push(hash[0])
        fH2.push(hash[1])
        fHits.push(0)
        entries++
      },
    })
  }

  /**
   * Walk the view in order, pairing each row with the uploaded file.
   *
   * Rows are read a page at a time through the view's own reader, so this costs
   * one more pass over whatever the table is already showing.
   */
  async function matchView() {
    const total = view.count()
    job.viewRows = total
    // Keys the view has that the file does not. Only these need tracking to spot
    // a repeat on the view side; the ones the file has are counted on its entry.
    const missingKeys = new Set()

    for (let start = 0; start < total; start += WALK_PAGE) {
      if (job.cancelled) throw new Error('Comparison cancelled.')
      const rows = await view.page(start, Math.min(WALK_PAGE, total - start))
      if (rows.length === 0) break
      for (let r = 0; r < rows.length; r++) {
        const row = rows[r]
        const pos = start + r
        const values = row.c
        const get = (k) => values[keyPairs[k].a] ?? ''
        const key = buildKey(get, keyPairs.length, norm)
        const entry = keys.get(key)

        if (entry === undefined) {
          if (missingKeys.has(key)) {
            push(V_REPEAT_VIEW, pos, row.i, NONE, -1)
            job.counts.repeatView++
          } else {
            if (missingKeys.size < MAX_KEYS) missingKeys.add(key)
            else job.keysCapped = true // repeats past the cap read as `missing`
            push(V_MISSING, pos, row.i, NONE, -1)
            job.counts.missing++
          }
          continue
        }

        if (fHits.get(entry) > 0) {
          // The file's row for this key is already spoken for by an earlier view
          // row, so this one has nothing of its own to compare against. It is not
          // given the file row either: there is no pairing to show.
          push(V_REPEAT_VIEW, pos, row.i, NONE, -1)
          job.counts.repeatView++
          continue
        }
        fHits.set(entry, 1)

        let skip = null
        if (tracksBlanks) {
          const base = entry * words
          for (let w = 0; w < words; w++) mask[w] = fBlank.get(base + w)
          skip = mask
        }
        contentHash((k) => values[compared[k].a] ?? '', compared.length, norm, skip, hash)

        const identical = hash[0] === fH1.get(entry) && hash[1] === fH2.get(entry)
        push(identical ? V_SAME : V_CHANGED, pos, row.i, fRowOf.get(entry), fOffOf.get(entry))
        if (identical) job.counts.same++
        else job.counts.changed++
      }
      job.walked = Math.min(total, start + rows.length)
    }

    // Whatever the view never claimed is in the file and not in the view.
    for (let e = 0; e < entries; e++) {
      if (fHits.get(e) === 0) {
        push(V_EXTRA, NONE, NONE, fRowOf.get(e), fOffOf.get(e))
        job.counts.extra++
      }
    }
    for (let r = 0; r < repeats; r++) {
      push(V_REPEAT_FILE, NONE, NONE, repRow.get(r), repOff.get(r))
      job.counts.repeatFile++
    }
  }

  const run = async () => {
    // The view's own scan has to finish first: its rows are what we compare, and
    // half of them is not a view.
    job.phase = 'view'
    await waitFor(view, job)
    job.capped = view.capped()

    job.phase = 'file'
    job.progress = 0
    const scan = indexFile()
    job.scan = scan
    const outcome = await scan.promise
    if (!outcome.completed) throw new Error('Comparison cancelled.')
    job.scan = null

    job.phase = 'match'
    job.progress = 0
    await matchView()
    release()
    job.phase = 'done'
    job.progress = 1
    job.done = true
  }

  job.ready = run().catch((err) => {
    job.error = err.message
    release()
    job.done = true
    job.phase = 'done'
  })

  return job
}

export function getCompareJob(dsView, dsFile, spec, dup, cmp) {
  const key = compareKey(spec, dup, dsFile.id, cmp, dsView)
  if (!dsView.compareJobs) dsView.compareJobs = new Map()
  let job = dsView.compareJobs.get(key)
  if (!job) {
    job = startCompareJob(dsView, dsFile, spec, dup, cmp, key)
    dsView.compareJobs.set(key, job)
    while (dsView.compareJobs.size > MAX_COMPARE_JOBS) {
      let oldestKey = null
      let oldest = null
      for (const [k, j] of dsView.compareJobs) if (!oldest || j.touched < oldest.touched) [oldestKey, oldest] = [k, j]
      oldest.cancelled = true
      oldest.scan?.abort()
      dsView.compareJobs.delete(oldestKey)
    }
  }
  job.touched = Date.now()
  return job
}

/** The result indices this mode lists, in result order. Built once per mode. */
function orderFor(job, mode) {
  const cached = job.orders.get(mode)
  if (cached) return cached
  const wanted = new Uint8Array(VERDICT_NAME.length)
  for (const v of MODE_VERDICTS[mode]) wanted[v] = 1
  let n = 0
  for (let i = 0; i < job.total; i++) if (wanted[job.verdict.get(i)]) n++
  const order = new Uint32Array(n)
  let p = 0
  for (let i = 0; i < job.total; i++) if (wanted[job.verdict.get(i)]) order[p++] = i
  job.orders.set(mode, order)
  return order
}

/** How many rows a mode lists. Zero until the scan finishes — nothing is partial. */
export function compareAvailable(job, mode) {
  if (!job.done || job.error) return 0
  return orderFor(job, mode).length
}

/**
 * Overall progress across the three phases, weighted by roughly what each costs:
 * waiting for the view's own scan, streaming the uploaded file, then walking the
 * view a page at a time.
 */
const PHASE_WEIGHT = { view: [0, 0.3], file: [0.3, 0.65], match: [0.65, 1], done: [1, 1] }

export function compareStatus(job, mode) {
  const [from, to] = PHASE_WEIGHT[job.phase] ?? [0, 1]
  const within =
    job.phase === 'view'
      ? job.view.progress()
      : job.phase === 'match'
        ? job.viewRows > 0
          ? job.walked / job.viewRows
          : 1
        : job.progress
  const c = job.counts
  return {
    filtered: true,
    compare: true,
    mode,
    phase: job.phase,
    fileName: job.dsFile.name,
    fileRows: job.fileRows,
    viewRows: job.viewRows,
    // What this mode lists, and what an export of it writes.
    matched: compareAvailable(job, mode),
    available: compareAvailable(job, mode),
    same: c.same,
    changed: c.changed,
    missing: c.missing,
    extra: c.extra,
    repeatView: c.repeatView,
    repeatFile: c.repeatFile,
    comparedColumns: job.cmp.compared.length,
    keyColumns: job.cmp.keyColumns,
    summary: describeCompare(job.cmp),
    done: job.done,
    progress: job.done ? 1 : Math.min(1, from + (to - from) * Math.min(1, within || 0)),
    capped: job.capped,
    keysCapped: job.keysCapped,
    error: job.error,
  }
}

/* ------------------------------------------------------------------- reading */

/**
 * Read a scattered set of view positions with as few reads as possible.
 *
 * Positions close together are fetched in one page and the rows in between are
 * thrown away, which is far cheaper than opening the file again — a page of
 * `changed` rows out of a mostly-unchanged file is otherwise one read per row.
 */
async function readViewPositions(job, positions) {
  const want = [...new Set(positions)].sort((a, b) => a - b)
  const out = new Map()
  let i = 0
  while (i < want.length) {
    const start = want[i]
    let end = start + 1
    let j = i + 1
    while (j < want.length && want[j] - end <= RUN_GAP && want[j] + 1 - start <= WALK_PAGE) {
      end = want[j] + 1
      j++
    }
    const rows = await job.view.page(start, end - start)
    for (let k = 0; k < rows.length; k++) out.set(start + k, rows[k])
    i = j
  }
  return out
}

/** Read a set of uploaded-file rows by byte offset, in offset order. */
async function readFileRows(dsFile, offsets) {
  const want = [...new Set(offsets)].sort((a, b) => a - b)
  const out = new Map()
  if (want.length === 0) return out
  const reader = await openRowReader(dsFile)
  try {
    for (const offset of want) {
      const fields = await reader.read(offset)
      if (fields) out.set(offset, fields)
    }
  } finally {
    await reader.close()
  }
  return out
}

/**
 * Which compared columns disagree, from the real values on both sides. This is
 * the answer the screen and the export both quote — the scan's hashes only ever
 * decided whether a pair was worth looking at.
 *
 * @returns {number[]} view column indices
 */
function diffColumns(cmp, norm, viewValues, fileValues) {
  const out = []
  for (const pair of cmp.compared) {
    if (!sameValue(viewValues[pair.a] ?? '', fileValues[pair.b] ?? '', norm, cmp.options.blanks)) {
      out.push(pair.a)
    }
  }
  return out
}

/** The uploaded row's values, laid out in the view's columns. */
function alignToView(cmp, viewWidth, fileValues) {
  const out = new Array(viewWidth).fill('')
  for (const pair of cmp.mapped) out[pair.a] = fileValues[pair.b] ?? ''
  return out
}

/**
 * One page of the comparison.
 *
 * Each row carries the view's values (`c`) as the base, the uploaded file's
 * values in the view's columns (`o`) where there is a counterpart, and which
 * columns differ (`d`). A row that exists on one side only carries that side's
 * values in `c` and says which side it is.
 */
export async function readComparePage(dsView, job, mode, offset, limit) {
  if (!job.done || job.error || limit <= 0) return []
  const order = orderFor(job, mode)
  const to = Math.min(offset + limit, order.length)
  if (offset >= to) return []

  const wantView = []
  const wantFile = []
  for (let p = offset; p < to; p++) {
    const i = order[p]
    if (job.vPos.get(i) !== NONE) wantView.push(job.vPos.get(i))
    const off = job.fOff.get(i)
    if (off >= 0) wantFile.push(off)
  }

  const viewRows = await readViewPositions(job, wantView)
  const fileRows = await readFileRows(job.dsFile, wantFile)
  const width = dsView.headers.length

  const out = []
  for (let p = offset; p < to; p++) {
    const i = order[p]
    const verdict = job.verdict.get(i)
    const pos = job.vPos.get(i)
    const vRow = job.vRow.get(i)
    const fRow = job.fRow.get(i)
    const fOff = job.fOff.get(i)

    const viewRow = pos === NONE ? null : viewRows.get(pos)
    // A page read that came back out of step with the scan must not paint one
    // row's values onto another's verdict.
    const viewValues = viewRow && viewRow.i === vRow ? viewRow.c : null
    const fileValues = fOff >= 0 ? (fileRows.get(fOff) ?? null) : null

    const row = {
      v: VERDICT_NAME[verdict],
      side: VERDICT_SIDE[verdict],
      i: pos === NONE ? null : vRow,
      j: fRow === NONE ? null : fRow,
      c: null,
      o: null,
      d: [],
    }

    if (verdict === V_EXTRA || verdict === V_REPEAT_FILE) {
      row.c = fileValues ? alignToView(job.cmp, width, fileValues) : null
    } else {
      row.c = viewValues
      if ((verdict === V_SAME || verdict === V_CHANGED) && viewValues && fileValues) {
        row.o = alignToView(job.cmp, width, fileValues)
        row.d = diffColumns(job.cmp, job.norm, viewValues, fileValues)
        // The scan compared hashes; this compared the values. They agree unless a
        // 64-bit hash collided, and if they ever do, the values win.
        if (row.d.length === 0) row.v = 'same'
        else row.v = 'changed'
      }
    }
    out.push(row)
  }
  return out
}

/* -------------------------------------------------------------------- export */

/**
 * The row's key, for the per-cell export. `c` is always laid out in the view's
 * columns — including for a row that came from the uploaded file — so the
 * view-side index reads it whichever side the row is from.
 */
function keyOf(cmp, row) {
  const values = row.c ?? []
  return cmp.keyPairs.map((p) => values[p.a] ?? '').join(' | ')
}

/**
 * Stream the comparison as CSV, in one of two shapes.
 *
 * `rows` is a record per row — the view's values, or the uploaded file's for a
 * row that only exists there — with the verdict, both row numbers and the names
 * of the columns that differ. It is the shape to hand back to whoever produced
 * the file.
 *
 * `cells` is a row per differing value: the key, the column, and what each side
 * says. It is the shape to work through, and the one that answers "what actually
 * changed" without reading across a wide table.
 */
export async function exportCompare(dsView, job, mode, shape, columnNames, out, serializeRow, range = {}) {
  if (!job.done) throw httpError(409, 'Comparison is still running — try again when it finishes.')
  if (job.error) throw httpError(500, job.error)

  const cmp = job.cmp
  const delimiter = dsView.delimiter
  const order = orderFor(job, mode)
  const skip = Math.max(0, Math.floor(Number(range.offset) || 0))
  const take = Math.max(0, Math.floor(Number(range.limit) || 0))
  const to = take > 0 ? Math.min(order.length, skip + take) : order.length

  const perCell = shape === 'cells'
  const all = !columnNames || columnNames.length === 0
  const indices = all
    ? dsView.headers.map((_, i) => i)
    : columnNames.map((nm) => dsView.headers.indexOf(nm)).filter((i) => i >= 0)

  out.write(
    serializeRow(
      perCell
        ? ['status', 'key', 'column', 'view_value', 'file_value', 'view_row', 'file_row']
        : ['status', 'side', 'view_row', 'file_row', 'changed_columns', ...indices.map((i) => dsView.headers[i])],
      delimiter,
    ),
  )
  if (skip >= to) return { completed: true }

  const byIndex = new Map(dsView.headers.map((h, i) => [i, h]))

  for (let start = skip; start < to; start += WALK_PAGE) {
    const rows = await readComparePage(dsView, job, mode, start, Math.min(WALK_PAGE, to - start))
    for (const row of rows) {
      if (perCell) {
        // Only a changed pair has cells to list; the other verdicts are about the
        // row existing at all, which the `rows` shape is for.
        if (row.v !== 'changed' || !row.c || !row.o) continue
        const key = keyOf(cmp, row)
        for (const c of row.d) {
          out.write(
            serializeRow(
              [
                row.v,
                key,
                byIndex.get(c) ?? `column_${c + 1}`,
                row.c[c] ?? '',
                row.o[c] ?? '',
                row.i === null ? '' : String(row.i + 1),
                row.j === null ? '' : String(row.j + 1),
              ],
              delimiter,
            ),
          )
        }
        continue
      }

      const values = row.c ?? []
      const changed = row.d.map((c) => byIndex.get(c) ?? `column_${c + 1}`).join('; ')
      out.write(
        serializeRow(
          [
            row.v,
            row.side,
            row.i === null ? '' : String(row.i + 1),
            row.j === null ? '' : String(row.j + 1),
            changed,
            ...indices.map((i) => values[i] ?? ''),
          ],
          delimiter,
        ),
      )
    }
  }
  return { completed: true }
}
